import { useQueryClient } from '@tanstack/react-query';
import Modal from 'antd/es/modal';
import { useState } from 'react';
import { useSelector } from 'react-redux';

import type { User } from 'api';
import { usePutCustomerUsersUserId } from 'api';
import { Restricted } from 'containers/Restricted';

import { selectUserId } from 'reduxLocal/slices/authSlice';

import { useOrganisationId } from 'hooks/context';

import { PrimaryButton } from '@atoms/buttons/PrimaryButton';
import { SecondaryButton } from '@atoms/buttons/SecondaryButton';
import { FormattedDate } from '@atoms/FormattedDate/FormattedDate';

import { userManagementDetailsT as translations } from './translations';
import { UserDetailsLabel } from './UserDetailsLabel';

import {
  USER_DETAIL_DEACTIVATE_FEEDBACK_MODAL_TEST_ID,
  USER_DETAILS_DEACTIVATE_BUTTON_TEST_ID,
  USER_DETAILS_DEACTIVATE_CANCEL_BUTTON_TEST_ID,
  USER_DETAILS_DEACTIVATE_CONFIRM_BUTTON_TEST_ID,
  USER_DETAILS_DEACTIVATE_CONFIRM_MODAL_TEST_ID,
  USER_DETAILS_EDIT_BUTTON_TEST_ID
} from '../constants';
import { ContactSupportCTA } from '../shared/ContactSupportCTA';
import { FeedbackModal } from '../shared/FeedbackModal';
import { RoleLabel } from '../shared/RoleLabel';

interface UserDetailsProps {
  data: User;
  onEditClick?: () => void;
}

type ResultState =
  | { open: false }
  | {
      open: true;
      title: string;
      content: React.ReactNode;
    };

export const UserDetailsShow = ({ data, onEditClick }: UserDetailsProps) => {
  const organisationId = useOrganisationId<number>();
  const userId = useSelector(selectUserId);
  const queryClient = useQueryClient();
  const mutation = usePutCustomerUsersUserId();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [result, setResult] = useState<ResultState>({ open: false });

  const handleDeactivate = async () => {
    try {
      await mutation.mutateAsync({
        organisationId,
        userId: data.id,
        data: {
          data: {
            first_name: data.first_name,
            last_name: data.last_name,
            email: data.email,
            role: data.role,
            active: false
          }
        }
      });

      queryClient.invalidateQueries({
        queryKey: ['users', organisationId],
        refetchType: 'active'
      });

      setResult({
        open: true,
        title: 'User deactivated',
        content: (
          <div className="space-y-3">
            <p>
              {data.email} {translations.deactivate.result.successMessageSuffix}
            </p>
          </div>
        )
      });
    } catch (_) {
      setResult({
        open: true,
        title: translations.deactivate.result.errorTitle,
        content: (
          <div className="space-y-3">
            <p>{translations.deactivate.result.errorMessage}</p>
          </div>
        )
      });
    } finally {
      setConfirmOpen(false);
    }
  };

  return (
    <>
      <div className="flex flex-col gap-3">
        <UserDetailsLabel
          label={translations.fields.firstName}
          copyText={data.first_name}
        >
          {data.first_name}
        </UserDetailsLabel>

        <UserDetailsLabel
          label={translations.fields.lastName}
          copyText={data.last_name}
        >
          {data.last_name}
        </UserDetailsLabel>

        <UserDetailsLabel label="E-mail" copyText={data.email}>
          {data.email}
        </UserDetailsLabel>

        <UserDetailsLabel label={translations.fields.role}>
          <RoleLabel role={data.role} />
        </UserDetailsLabel>

        <UserDetailsLabel label={translations.fields.created}>
          <FormattedDate
            value={data.created_at}
            className="text-wrap font-medium"
          />
        </UserDetailsLabel>

        <div className="m-2 border-t" />

        <Restricted requiredPermission="users:manage">
          <SecondaryButton
            size="small"
            className="h-9"
            htmlType="button"
            onClick={onEditClick}
            data-testid={USER_DETAILS_EDIT_BUTTON_TEST_ID}
          >
            {translations.actions.edit}
          </SecondaryButton>

          {/* Let's hide deactive button for user's own record  */}
          {userId !== data.id && (
            <PrimaryButton
              danger
              size="small"
              className="h-9"
              htmlType="button"
              onClick={() => setConfirmOpen(true)}
              loading={mutation.isPending}
              data-testid={USER_DETAILS_DEACTIVATE_BUTTON_TEST_ID}
            >
              {translations.actions.deactivateUser}
            </PrimaryButton>
          )}
        </Restricted>
      </div>

      <Modal
        data-testid={USER_DETAILS_DEACTIVATE_CONFIRM_MODAL_TEST_ID}
        open={confirmOpen}
        centered
        closable={false}
        onCancel={() => setConfirmOpen(false)}
        title={
          <div className="text-lg font-medium">
            {translations.deactivate.confirmModal.title}
          </div>
        }
        footer={
          <div className="flex justify-end gap-3">
            <SecondaryButton
              data-testid={USER_DETAILS_DEACTIVATE_CONFIRM_BUTTON_TEST_ID}
              size="small"
              className="h-9"
              onClick={() => setConfirmOpen(false)}
            >
              {translations.actions.cancel}
            </SecondaryButton>

            <PrimaryButton
              data-testid={USER_DETAILS_DEACTIVATE_CANCEL_BUTTON_TEST_ID}
              danger
              size="small"
              className="h-9"
              loading={mutation.isPending}
              onClick={handleDeactivate}
            >
              {translations.actions.deactivateUser}
            </PrimaryButton>
          </div>
        }
      >
        <div className="space-y-3 text-sm">
          <p>
            {translations.deactivate.confirmModal.body.line1Prefix}
            <span className="font-medium">
              {data.first_name} {data.last_name}
            </span>
            ?
          </p>
          <p>{translations.deactivate.confirmModal.body.line2}</p>
          <ContactSupportCTA
            reachOutPrefix={translations.deactivate.confirmModal.body.reachOut}
          />
        </div>
      </Modal>

      {result.open && (
        <FeedbackModal
          testId={USER_DETAIL_DEACTIVATE_FEEDBACK_MODAL_TEST_ID}
          open={result.open}
          title={result.title}
          message={result.content}
          onClose={() => setResult({ open: false })}
        />
      )}
    </>
  );
};
